import React, { useCallback } from 'react';
import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import styles from './style';

interface FilterOption {
  label: string;
  value: string;
}

interface CustomSelectProps {
  // Opens above the select with all its options (at the bottom of a drawer).
  openUp?: boolean;
  // Small uppercase label shown above the value. Optional for a form field with its own label.
  label?: string;
  // Label of the empty value (ex: "Tous").
  emptyLabel?: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = React.memo((props) => {
  const {
    label,
    emptyLabel,
    options,
    value,
    onChange,
    disabled,
    openUp,
  } = props;

  const onSelect = useCallback((event: SelectChangeEvent) => {
    onChange(event.target.value);
  }, [onChange]);

  const renderValue = useCallback((selected: string) => {
    const option = options.find((o) => o.value === selected);
    let text = emptyLabel;
    if (option) {
      text = option.label;
    }
    return (
      <>
        {label && <Typography component="span" sx={styles.label}>{label}</Typography>}
        <Typography component="span" sx={styles.value}>
          {text}
        </Typography>
      </>
    );
  }, [emptyLabel, label, options]);

  return (
    <Select
      value={value}
      onChange={onSelect}
      displayEmpty
      disabled={disabled}
      renderValue={renderValue}
      IconComponent={KeyboardArrowDown}
      sx={styles.select}
      MenuProps={openUp ? styles.menuPropsUp : styles.menuProps}
    >
      {emptyLabel !== undefined && (
        <MenuItem value="" sx={styles.menuItem}>{emptyLabel}</MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} sx={styles.menuItem}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

export type { FilterOption };

export default CustomSelect;
